import React, { forwardRef } from 'react';
import { X } from 'lucide-react';
import { cn } from './tokens';

// =============================================================================
// BADGE VARIANTS
// =============================================================================

const variants = {
  // Solid backgrounds
  default: 'bg-slate-800 text-slate-300 border border-slate-700',
  primary: 'bg-accent text-black',
  success: 'bg-green-600 text-white',
  warning: 'bg-amber-600 text-black',
  danger: 'bg-red-600 text-white',
  info: 'bg-blue-600 text-white',

  // Muted/subtle backgrounds
  'default-muted': 'bg-slate-800/50 text-slate-400 border border-slate-700/50',
  'primary-muted': 'bg-accent/10 text-accent border border-accent/30',
  'success-muted': 'bg-green-500/10 text-status-success border border-green-500/30',
  'warning-muted': 'bg-amber-500/10 text-status-warning border border-amber-500/30',
  'danger-muted': 'bg-red-500/10 text-status-danger border border-red-500/30',
  'info-muted': 'bg-blue-500/10 text-status-info border border-blue-500/30',

  // Outline only
  outline: 'bg-transparent text-slate-400 border border-slate-600',
  'outline-primary': 'bg-transparent text-accent border border-accent',
  'outline-success': 'bg-transparent text-status-success border border-green-500',
  'outline-warning': 'bg-transparent text-status-warning border border-amber-500',
  'outline-danger': 'bg-transparent text-status-danger border border-red-500',
} as const;

const sizes = {
  xs: 'text-[11px] px-1.5 py-0.5 gap-1',
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-xs px-2.5 py-1 gap-1.5',
  lg: 'text-sm px-3 py-1.5 gap-2',
} as const;

// =============================================================================
// TYPES
// =============================================================================

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Visual style variant */
  variant?: keyof typeof variants;
  /** Size of the badge */
  size?: keyof typeof sizes;
  /** Make the badge pill-shaped */
  pill?: boolean;
  /** Icon to display before text */
  leftIcon?: React.ReactNode;
  /** Icon to display after text */
  rightIcon?: React.ReactNode;
  /** Show removable X button */
  removable?: boolean;
  /** Callback when remove button clicked */
  onRemove?: () => void;
}

// =============================================================================
// BADGE COMPONENT
// =============================================================================

/**
 * Badge component for labels, tags, and status indicators
 *
 * @example
 * // Basic badge
 * <Badge>Default</Badge>
 *
 * @example
 * // Status badge
 * <Badge variant="success-muted">Active</Badge>
 *
 * @example
 * // Removable tag
 * <Badge variant="primary-muted" removable onRemove={() => handleRemove()}>
 *   Southern Rock
 * </Badge>
 *
 * @example
 * // With icon
 * <Badge variant="info" leftIcon={<Clock size={12} />}>
 *   2 hours ago
 * </Badge>
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'default',
      size = 'sm',
      pill = false,
      leftIcon,
      rightIcon,
      removable = false,
      onRemove,
      className,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <span
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center font-medium',
          // Border radius
          pill ? 'rounded-full' : 'rounded-md',
          // Variant styles
          variants[variant],
          // Size styles
          sizes[size],
          // Custom classes
          className
        )}
        {...props}
      >
        {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}

        <span className="truncate">{children}</span>

        {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}

        {removable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            className={cn(
              'flex-shrink-0 rounded-full p-0.5 -mr-0.5',
              'hover:bg-white/20 transition-colors',
              'focus:outline-none focus:ring-1 focus:ring-white/50'
            )}
            aria-label="Remove"
          >
            <X size={size === 'xs' ? 10 : size === 'sm' ? 12 : 14} />
          </button>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// =============================================================================
// STATUS DOT
// =============================================================================

const dotColors = {
  default: 'bg-slate-400',
  success: 'bg-green-400',
  warning: 'bg-amber-400',
  danger: 'bg-red-400',
  info: 'bg-blue-400',
  accent: 'bg-accent',
} as const;

interface StatusDotProps {
  /** Color of the dot */
  color?: keyof typeof dotColors;
  /** Size of the dot */
  size?: 'sm' | 'md' | 'lg';
  /** Animate with pulse effect */
  pulse?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Simple status dot indicator
 *
 * @example
 * <StatusDot color="success" />
 * <StatusDot color="danger" pulse />
 */
export const StatusDot: React.FC<StatusDotProps> = ({
  color = 'default',
  size = 'md',
  pulse = false,
  className,
}) => {
  const sizeMap = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-3 h-3',
  };

  return (
    <span className={cn('relative inline-flex', className)}>
      <span
        className={cn(
          'rounded-full',
          sizeMap[size],
          dotColors[color],
          pulse && 'animate-ping absolute inline-flex h-full w-full opacity-75'
        )}
      />
      {pulse && (
        <span
          className={cn(
            'relative inline-flex rounded-full',
            sizeMap[size],
            dotColors[color]
          )}
        />
      )}
    </span>
  );
};

// =============================================================================
// TAG GROUP
// =============================================================================

interface TagGroupProps {
  /** Tags to display */
  children: React.ReactNode;
  /** Maximum tags to show before "+N more" */
  maxVisible?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Group of tags with overflow handling
 *
 * @example
 * <TagGroup maxVisible={3}>
 *   <Badge>Tag 1</Badge>
 *   <Badge>Tag 2</Badge>
 *   <Badge>Tag 3</Badge>
 *   <Badge>Tag 4</Badge>
 * </TagGroup>
 */
export const TagGroup: React.FC<TagGroupProps> = ({
  children,
  maxVisible,
  className,
}) => {
  const childArray = React.Children.toArray(children);
  const visibleChildren = maxVisible ? childArray.slice(0, maxVisible) : childArray;
  const hiddenCount = maxVisible ? childArray.length - maxVisible : 0;

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {visibleChildren}
      {hiddenCount > 0 && (
        <Badge variant="default-muted" size="sm">
          +{hiddenCount} more
        </Badge>
      )}
    </div>
  );
};

export default Badge;
